import {original, produce} from 'immer';

import {startFunctionPerformanceMeasure} from '../../../../util/datadog/workspace/functionPerformance';

// This function is called with two different query result types — BucketedQueryResult
// (run has `bucketedHistory`) and BackendLinesQueryResult (run has `lines`). Their run
// shapes differ, so neither works as a shared parameter type. AnyDeltaQueryResult also
// doesn't work because the grouped variants lack `runs` entirely. A hand-written
// structural type scoped to the three fields this function actually reads is the
// simplest correct solution.
interface DeltaShape {
  order?: string[] | null;
  delta?: Array<{run?: {id: string} | null} | null> | null;
  incomplete?: string[] | null;
}
interface PinnedSubQueryResult {
  data?: {
    project?: {
      runs?: {deltas?: DeltaShape | null} | null;
      // legacy path: pinnedRuns lives under project
      pinnedRuns?: {deltas?: DeltaShape | null} | null;
    } | null;
    // cross-project path: pinnedRuns lives at top level
    pinnedRuns?: {deltas?: DeltaShape | null} | null;
  } | null;
}

/**
 * Applies the pinnedRuns sub-query result into the main runs result in place,
 * prepending pinned/baseline runs to the front of the order and delta arrays.
 *
 * The pinnedRuns field is fetched via a GraphQL alias on the same Project type,
 * filtered to the pinned/baseline run names only.
 *
 * Visibility is already enforced at query time: the pinnedRunFilters variable
 * ANDs the pinned-names filter with the user's selections filter, so hidden
 * pinned runs are simply not fetched and won't appear here.
 *
 * Handles two result shapes depending on which query path was used:
 *   - Standard path: pinnedRuns lives at `data.pinnedRuns` (top-level
 *     `runs(ids:)` alias). Used by all non-internal-id queries.
 *   - Legacy path: pinnedRuns lives at `data.project.pinnedRuns`
 *     (project-scoped `runs(filters:)` alias). Still used by the
 *     `*_INTERNAL_ID` queries that have not been migrated yet.
 *
 * Note: baseline runs are included in the pinnedRuns sub-query (they share the
 * same ID/filter list), so there is no separate baselineRun alias. The
 * isBaseline / isPinned flags on rendered lines are computed independently in
 * useBucketedData.ts from the run IDs.
 */
export function applyPinnedSubQuery(result: null | undefined): null | undefined;
export function applyPinnedSubQuery<T extends PinnedSubQueryResult>(
  result: T
): T;
export function applyPinnedSubQuery<T extends PinnedSubQueryResult>(
  result: T | null | undefined
): T | null | undefined {
  if (!result) {
    return result;
  }

  const measureMainDeltas = result.data?.project?.runs?.deltas;
  const measurePinnedDeltas =
    result.data?.pinnedRuns?.deltas ?? result.data?.project?.pinnedRuns?.deltas;
  // Build bounded context before starting the timer so telemetry bookkeeping
  // is not attributed to the Immer transaction being measured.
  const pinnedIncompleteCount = measurePinnedDeltas?.incomplete?.length ?? 0;
  const pinnedOrderCount = measurePinnedDeltas?.order?.length ?? 0;
  let mergeKind = 'noChanges';
  if (!measureMainDeltas || !measurePinnedDeltas) {
    mergeKind = 'missingDeltas';
  } else if (pinnedOrderCount > 0) {
    mergeKind = 'merged';
  } else if (pinnedIncompleteCount > 0) {
    mergeKind = 'incompleteOnly';
  }
  const measureContext = {
    implementation: 'immer',
    mergeKind,
    mainDeltaCount: measureMainDeltas?.delta?.length ?? 0,
    mainIncompleteCount: measureMainDeltas?.incomplete?.length ?? 0,
    mainOrderCount: measureMainDeltas?.order?.length ?? 0,
    pinnedDeltaCount: measurePinnedDeltas?.delta?.length ?? 0,
    pinnedIncompleteCount,
    pinnedOrderCount,
  };
  const endMeasure = startFunctionPerformanceMeasure('applyPinnedSubQuery');

  const nextResult = produce(result, draft => {
    const mainDeltas = draft.data?.project?.runs?.deltas;
    // Support both result shapes: cross-project path puts pinnedRuns at the top
    // level; legacy path puts it under project.
    const rawPinnedDeltas =
      draft.data?.pinnedRuns?.deltas ?? draft.data?.project?.pinnedRuns?.deltas;
    const pinnedDeltas = rawPinnedDeltas ? original(rawPinnedDeltas) : null;

    if (!mainDeltas || !pinnedDeltas) {
      return;
    }

    // Propagate incomplete run IDs from the pinned sub-query so the follow-up
    // fetch logic in bucketedDeltaQuery.ts handles them the same way as
    // incomplete regular runs.
    //
    // Need to add incomplete runs BEFORE the possible early return for empty order below.
    // Otherwise if we did actually try to fetch pinnedRuns but none of them compelted in time,
    // fetchIncompleteRuns wouldn't try to fetch any of the runs again.
    if (pinnedDeltas.incomplete?.length) {
      mainDeltas.incomplete = [
        ...pinnedDeltas.incomplete,
        ...(mainDeltas.incomplete ?? []),
      ];
    }

    const pinnedOrder: string[] = pinnedDeltas.order ?? [];
    if (pinnedOrder.length === 0) {
      return;
    }

    mainDeltas.order = [...pinnedOrder, ...(mainDeltas.order ?? [])];
    mainDeltas.delta = [
      ...(pinnedDeltas.delta ?? []),
      ...(mainDeltas.delta ?? []),
    ];
    delete draft.data?.pinnedRuns;
    delete draft.data?.project?.pinnedRuns;
  });
  endMeasure(measureContext);
  return nextResult;
}
